import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { socialLinks } from '../data/mock';
import { Menu, X, Globe, ChevronDown, Instagram, Facebook } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'about', href: '#about' },
    { key: 'locations', href: '#locations' },
    { key: 'menu', href: '#menu' },
    { key: 'gallery', href: '#gallery' },
    { key: 'reviews', href: '#reviews' },
  ];

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.06)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center gap-3 group"
            >
              <img
                src="/assets/images/logo/logo.png"
                alt="Cristian's Baja Salada"
                className={`w-auto transition-all duration-300 ${
                  isScrolled ? 'h-12' : 'h-16 brightness-[1.3] drop-shadow-[0_0_12px_rgba(255,255,255,0.3)]'
                }`}
              />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => scrollToSection(item.href)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-white/10 ${
                    isScrolled
                      ? 'text-[#334155] hover:text-[#0D9488] hover:bg-[#0D9488]/5'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {t(`nav.${item.key}`)}
                </button>
              ))}

              {/* Language Selector */}
              <div className="relative ml-3">
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  onBlur={() => setTimeout(() => setLangDropdownOpen(false), 200)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
                    isScrolled
                      ? 'text-[#334155] border-[#E2E8F0] hover:border-[#0D9488] hover:text-[#0D9488]'
                      : 'text-white border-white/30 hover:border-white/60'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="uppercase">{language}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {langDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-36 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                    >
                      <button
                        onClick={() => { setLanguage('en'); setLangDropdownOpen(false); }}
                        className={`w-full px-4 py-3 text-sm text-left flex items-center gap-2 transition-colors ${
                          language === 'en' ? 'bg-[#0D9488]/5 text-[#0D9488] font-semibold' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-base">EN</span> English
                      </button>
                      <button
                        onClick={() => { setLanguage('es'); setLangDropdownOpen(false); }}
                        className={`w-full px-4 py-3 text-sm text-left flex items-center gap-2 transition-colors ${
                          language === 'es' ? 'bg-[#0D9488]/5 text-[#0D9488] font-semibold' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-base">ES</span> Español
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Social Icons */}
              <div className="flex items-center gap-1.5 ml-2">
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isScrolled
                      ? 'text-[#334155] hover:text-[#E1306C] hover:bg-[#E1306C]/5'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isScrolled
                      ? 'text-[#334155] hover:text-[#1877F2] hover:bg-[#1877F2]/5'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-[#0C4A6E]' : 'text-white'
              }`}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl"
            >
              <div className="p-6 pt-24">
                <nav className="flex flex-col gap-1">
                  {navItems.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => scrollToSection(item.href)}
                      className="px-4 py-3 text-left text-[#334155] font-medium rounded-xl hover:bg-[#0D9488]/5 hover:text-[#0D9488] transition-colors"
                    >
                      {t(`nav.${item.key}`)}
                    </button>
                  ))}
                </nav>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 px-4">{language === 'en' ? 'Language' : 'Idioma'}</p>
                  <div className="flex gap-2 px-4">
                    <button
                      onClick={() => setLanguage('en')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        language === 'en' ? 'bg-[#0D9488] text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      English
                    </button>
                    <button
                      onClick={() => setLanguage('es')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        language === 'es' ? 'bg-[#0D9488] text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      Español
                    </button>
                  </div>
                </div>
                {/* Social Icons - Mobile */}
                <div className="mt-6 pt-6 border-t border-gray-100 px-4">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">{language === 'en' ? 'Follow Us' : 'Síguenos'}</p>
                  <div className="flex gap-3">
                    <a
                      href={socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-[#E1306C] hover:bg-[#E1306C]/10 transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href={socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-[#1877F2] hover:bg-[#1877F2]/10 transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
